/**
 * Copyright (c) Microsoft. All rights reserved.
 */

export const INVALID_INDEX = -1;

/**
 * Represents the state of the labeling reducer.
 */
export type LabelingState = {
    /**
     * Labeling pin point.
     */
    labelingPinPoint: number;

    /**
     * Indicates at which index the labeling ends.
     */
    labelEnd: number;

    /**
     * Indicates at which index the labeling starts.
     */
    labelStart: number;

    /**
     * Indicates at which index the hovering starts.
     */
    hoverStart: number;

    /**
     * Indicates at which index the hovering ends.
     */
    hoverEnd: number;

    /**
     * If true labeling will be disabled.
     */
    disabled: boolean;

    /**
     * Indicates whether the user is dragging their mouse over
     * the utterance or not.
     */
    dragging: boolean;
};

/**
 * Represents all the action functions that can be applied to the state.
 */
export type LabelingActions = {
    /**
     * Request hovering a specific token.
     */
    hover: (index: number) => void;

    /**
     * Request labeling a specific token.
     */
    label: (index: number, isKeyboardInteraction?: boolean) => void;

    /**
     * Request labeling a specific token.
     */
    labelLeft: (index: number) => void;

    /**
     * Request labeling a specific token.
     */
    labelRight: (index: number) => void;

    /**
     * Resets the hovering state.
     */
    unHover: () => void;

    /**
     * Resets the labeling state.
     */
    cancel: () => void;

    /**
     * Sets the labeling dragging state to false.
     */
    stopDragging: () => void;

    /**
     * Selects all tokens.
     */
    selectAll: (lastIndex: number) => void;
};

/**
 * An enum representing all the action types that can be applied to the state.
 */
export type LabelingActionType =
    | 'Hover'
    | 'UnHover'
    | 'Label'
    | 'Cancel'
    | 'LabelLeft'
    | 'LabelRight'
    | 'UpdateState'
    | 'StopDragging'
    | 'SetDisabled'
    | 'SelectAll';

type ActionParams = { params: { index: number; isKeyboardInteraction?: boolean } };

type ObjectType<T extends LabelingActionType> = { type: T };

type HoverAction = (ObjectType<'Hover'> & ActionParams) | ObjectType<'UnHover'>;
type LabelAction = (ObjectType<'Label'> & ActionParams) | ObjectType<'Cancel'>;
type KeyboardAction = ObjectType<'LabelLeft' | 'LabelRight'> & ActionParams;
type UpdateStateAction = ObjectType<'UpdateState'> & { params: Partial<LabelingState> };
type SetDisabledAction = ObjectType<'SetDisabled'> & { params: { disabled: boolean } };
type SelectAllAction = ObjectType<'SelectAll'> & { params: { lastIndex: number } };

export type LabelingAction =
    | HoverAction
    | LabelAction
    | KeyboardAction
    | UpdateStateAction
    | ObjectType<'StopDragging'>
    | SetDisabledAction
    | SelectAllAction;

export const getDefaultState = (disabled: boolean): LabelingState => ({
    disabled,
    dragging: false,
    hoverEnd: INVALID_INDEX,
    hoverStart: INVALID_INDEX,
    labelEnd: INVALID_INDEX,
    labelStart: INVALID_INDEX,
    labelingPinPoint: INVALID_INDEX
});

/**
 * A reducer that decides the next labeling state based on a previous state and some action.
 */
export const labelingReducer = (state: LabelingState, action: LabelingAction): LabelingState => {
    let newState: LabelingState = state;

    switch (action.type) {
        case 'Hover':
        case 'UnHover':
            newState = hoverReducer(state, action);
            break;
        case 'Label':
        case 'Cancel':
            newState = labelReducer(state, action);
            break;
        case 'LabelLeft':
            newState = labelReducer(state, { type: 'Label', params: { index: action.params.index, isKeyboardInteraction: true } });
            break;
        case 'LabelRight':
            newState = labelReducer(state, { type: 'Label', params: { index: action.params.index, isKeyboardInteraction: true } });
            break;
        case 'StopDragging':
            newState = { ...state, dragging: false };
            break;

        case 'SetDisabled':
            return getDefaultState(action.params.disabled);

        case 'SelectAll': {
            if (state.labelStart !== INVALID_INDEX) {
                return { ...state, labelStart: 0, labelEnd: action.params.lastIndex };
            }

            return state;
        }

        case 'UpdateState':
            newState = {
                ...state,
                ...action.params
            };
            break;
        default:
            newState = state;
    }

    if (state.disabled) {
        return state;
    }

    return newState;
};

/**
 * A sub reducer that decides the next labeling state.
 */
const labelReducer = (state: LabelingState, action: LabelAction): LabelingState => {
    if (action.type === 'Cancel') {
        // If the labeling state is already reset return the same object to avoid unnecessary renders
        if (state.labelEnd === INVALID_INDEX && state.labelStart === INVALID_INDEX) {
            return state;
        }

        return { ...state, labelingPinPoint: INVALID_INDEX, labelEnd: INVALID_INDEX, labelStart: INVALID_INDEX, dragging: false };
    }

    const {
        params: { index, isKeyboardInteraction = false }
    } = action;
    const { labelingPinPoint } = state;

    const startedLabeling = labelingPinPoint !== INVALID_INDEX;

    const commonState: Partial<LabelingState> = { hoverStart: INVALID_INDEX, hoverEnd: INVALID_INDEX, dragging: !isKeyboardInteraction };

    if (startedLabeling) {
        if (index > labelingPinPoint) {
            return { ...state, ...commonState, labelEnd: index };
        }
        if (index < labelingPinPoint) {
            return { ...state, ...commonState, labelStart: index };
        }
        return { ...state, ...commonState, labelStart: index, labelEnd: index };
    }
    return { ...state, ...commonState, labelStart: index, labelEnd: index, labelingPinPoint: index };
};

/**
 * A sub reducer that decides the next hovering state.
 */
const hoverReducer = (state: LabelingState, action: HoverAction): LabelingState => {
    if (action.type === 'UnHover') {
        // If the hovering state is already reset return the same object to avoid unnecessary renders
        if (state.hoverEnd === INVALID_INDEX && state.hoverStart === INVALID_INDEX) {
            return state;
        }

        return { ...state, hoverEnd: INVALID_INDEX, hoverStart: INVALID_INDEX };
    }

    const {
        params: { index }
    } = action;
    const { labelEnd, labelStart } = state;

    const startedLabeling = labelStart !== INVALID_INDEX;

    if (startedLabeling) {
        if (index < labelStart) {
            return { ...state, hoverStart: index };
        }
        if (index > labelEnd) {
            return { ...state, hoverEnd: index };
        }
        return { ...state, hoverEnd: INVALID_INDEX, hoverStart: INVALID_INDEX };
    }

    return { ...state, hoverStart: index };
};
